import React, { FC, useCallback, useRef, useState } from 'react';
import classnames from 'classnames';
import { useIntl } from 'react-intl';

import { Popup } from '@/scenarios/legacy-player/components/PlayerSkin/parts/Popup';
import { Settings } from '@/scenarios/legacy-player/components/PlayerSkin/parts/Settings/Settings';
import { Option } from '@/scenarios/legacy-player/components/PlayerSkin/parts/OptionList';
import { useCurrentPlaybackRate } from '@/scenarios/legacy-player/hooks/useCurrentPlaybackRate';
import { useCurrentVideoTrack } from '@/scenarios/legacy-player/hooks/useCurrentVideoTrack';
import { useInstanceCallback } from '@/scenarios/legacy-player/hooks/useInstanceCallback';
import { usePlaybackRateRange } from '@/scenarios/legacy-player/hooks/usePlaybackRateRange';
import { useVideoTracks } from '@/scenarios/legacy-player/hooks/useVideoTracks';

import { PlayerContentType } from '../../../types/content';
import { BaseControlProps } from '../../../types/controls';
import {
    useSubtitlesSettings,
    useSubtitlesSettingsSetters,
} from '../../../hooks/skin/use-subtitles';
import { usePlayerPopupButton } from '../../../hooks/skin/use-player-popup-button';

import { useChangeQuality } from './hooks/use-change-quality';
import styles from './styles.module.css';

interface Props extends BaseControlProps {
    contentType: PlayerContentType;
    isSubtitleSizeSettingsForceOpened?: boolean;
    setIsSubtitleSizeSettingsForceOpened?: (isSubtitleSizeSettingsForceOpened: boolean) => void;
    className?: string;
}

export const SettingPopupButton: FC<Props> = ({
    className,
    onClick,
    contentType,
    isSubtitleSizeSettingsForceOpened = false,
    setIsSubtitleSizeSettingsForceOpened,
}) => {
    const controlRef = useRef<HTMLDivElement>(null);

    const [isManuallyOpen, setIsManuallyOpen] = useState(false);
    const [isControlHovered, setIsControlHovered] = useState(false);

    const isOpen = isManuallyOpen || isSubtitleSizeSettingsForceOpened;

    const setIsOpen = useCallback(
        (newIsOpen: boolean) => {
            setIsManuallyOpen(newIsOpen);
            if (!newIsOpen) {
                setIsSubtitleSizeSettingsForceOpened?.(false);
            }
        },
        [setIsSubtitleSizeSettingsForceOpened]
    );

    const { handleClick } = usePlayerPopupButton({
        isOpen,
        setIsOpen,
        onClick,
    });

    const videoTracks = useVideoTracks();
    const currentVideoTrack = useCurrentVideoTrack();
    const currentRate = useCurrentPlaybackRate();
    const playbackRateRange = usePlaybackRateRange();

    const handleQualitySelect = useChangeQuality();

    const handleRateSelect = useInstanceCallback((instance, rateOption: Option<number>) => {
        instance?.setPlaybackRate(rateOption.value);
    });

    const { ratio } = useSubtitlesSettings();
    const { setSubtitlesRatio } = useSubtitlesSettingsSetters();

    const intl = useIntl();

    if (!videoTracks) {
        return null;
    }

    return (
        <div
            onMouseEnter={() => setIsControlHovered(true)}
            onMouseLeave={() => setIsControlHovered(false)}
            ref={controlRef}
            className={classnames(styles.root, className)}
        >
            <button
                onClick={handleClick}
                className={styles.settingsIcon}
                aria-expanded={isOpen}
                aria-label={intl.formatMessage({
                    id: 'features.player.f4f70727dc34561dfde1a3c529b6205c',
                    defaultMessage: 'Settings',
                    description: 'Settings label (player skin)',
                })}
            />
            <Popup
                targetHovered={isControlHovered}
                lockTargetHover={setIsOpen}
                visible={isOpen}
                targetRef={controlRef}
                placement="top"
                enableSeamlessResize
            >
                <Settings
                    videoTracks={videoTracks}
                    currentVideoTrack={currentVideoTrack}
                    currentRate={currentRate}
                    playbackRateRange={playbackRateRange}
                    playerContentType={contentType}
                    handleQualitySelect={handleQualitySelect}
                    handleRateSelect={handleRateSelect}
                    isSubtitlesSizeForced={isSubtitleSizeSettingsForceOpened}
                    setSubtitlesRatio={setSubtitlesRatio}
                    subtitlesRatio={ratio}
                />
            </Popup>
        </div>
    );
};
